"use client";

import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CardMedia,
  Grid,
  Snackbar,
  TextField,
  Container,
  Card,
  Chip,
  IconButton,
  Alert,
  Skeleton,
  Breadcrumbs,
  Link,
  Paper,
  Rating,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon,
  LocalShipping as ShippingIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";

const DetailPage = () => {
  const { medicineId } = useParams();
  const [medicine, setMedicine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [inCart, setInCart] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    const fetchMedicineDetails = async () => {
      if (!medicineId) {
        setError("No medicine ID provided.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `https://namami-infotech.com/EvaraBackend/src/sku/get_sku.php?medicine_id=${medicineId}`
        );
        const data = await response.json();
        if (data.message === "Medicine details fetched successfully") {
          setMedicine(data.medicine);
        } else {
          setError("Failed to fetch medicine details.");
        }
      } catch (err) {
        setError("An error occurred while fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchMedicineDetails();
  }, [medicineId]);

  useEffect(() => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const itemInCart = cart.find(
      (item) => item.id === Number.parseInt(medicineId)
    );

    if (itemInCart) {
      setInCart(true);
      setQuantity(itemInCart.quantity);
    } else {
      setInCart(false);
      setQuantity(1);
    }
  }, [medicineId]);

  const handleAddToCart = () => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const updatedCart = [
      ...cart,
      { id: Number.parseInt(medicineId), quantity },
    ];
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    setSnackbarMessage("Added to cart successfully!");
    setSnackbarOpen(true);
    setInCart(true);
  };

  const handleQuantityChange = (event) => {
    const value = Number.parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= 999) {
      setQuantity(value);
      updateCart(value);
    }
  };

  const incrementQuantity = () => {
    if (quantity < 999) {
      const newQuantity = quantity + 1;
      setQuantity(newQuantity);
      updateCart(newQuantity);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 0) {
      const newQuantity = quantity - 1;
      if (newQuantity > 0) {
        setQuantity(newQuantity);
        updateCart(newQuantity);
      } else {
        removeFromCart();
        setInCart(false);
        setQuantity(1);
      }
    }
  };

  const updateCart = (newQuantity) => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const updatedCart = cart.map((item) =>
      item.id === Number.parseInt(medicineId)
        ? { ...item, quantity: newQuantity }
        : item
    );
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  const removeFromCart = () => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const updatedCart = cart.filter(
      (item) => item.id !== Number.parseInt(medicineId)
    );
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const calculateDiscount = () => {
    if (medicine?.ptr && medicine?.mrp) {
      return Math.round(
        ((medicine.mrp - medicine.ptr) / medicine.mrp) * 100
      );
    }
    return 0;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Skeleton
              variant="rectangular"
              height={500}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={40} />
            <Skeleton variant="text" height={80} />
            <Skeleton
              variant="rectangular"
              height={200}
              sx={{ mt: 2, borderRadius: 2 }}
            />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        background:
          "linear-gradient(135deg, #008080 0%, #20B2AA 50%, #48D1CC 100%)",
        minHeight: "100vh",
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3, color: "white" }}
        >
          <Link
            color="inherit"
            href="/"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          
          <Typography color="white">{medicine?.name}</Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Box sx={{ position: "relative" }}>
                <CardMedia
                  component="img"
                  height="500"
                  image={medicine?.image_url || "/medicine-still-life.png"}
                  alt={medicine?.name}
                  sx={{
                    objectFit: "contain",
                    p: 2,
                    cursor: "zoom-in",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                />
                {calculateDiscount() > 0 && (
                  <Chip
                    label={`${calculateDiscount()}% OFF`}
                    color="error"
                    sx={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                    }}
                  />
                )}
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                p: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: "bold", color: "#008080", mb: 1 }}
                  >
                    {medicine?.name}
                  </Typography>
                  <Typography variant="h6" sx={{ color: "#666", mb: 2 }}>
                    by {medicine?.company_name}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    onClick={() => setIsFavorite(!isFavorite)}
                    sx={{
                      color: isFavorite ? "#ff4444" : "#ccc",
                      "&:hover": { color: "#ff4444" },
                    }}
                  >
                    <FavoriteIcon />
                  </IconButton>
                  <IconButton sx={{ color: "#008080" }}>
                    <ShareIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Rating value={4.5} precision={0.5} readOnly />
                <Typography variant="body2" sx={{ ml: 1, color: "#666" }}>
                  (4.5) • 127 reviews
                </Typography>
              </Box>

              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  background:
                    "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
                >
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: "bold", color: "#008080" }}
                  >
                    ₹{medicine?.ptr}
                  </Typography>
                  {medicine?.mrp !== medicine?.selling_price && (
                    <Typography
                      variant="h6"
                      sx={{
                        textDecoration: "line-through",
                        color: "#999",
                        fontWeight: "normal",
                      }}
                    >
                      ₹{medicine?.mrp}
                    </Typography>
                  )}
                  {calculateDiscount() > 0 && (
                    <Chip
                      label={`Save ${calculateDiscount()}%`}
                      color="success"
                      size="small"
                      sx={{ fontWeight: "bold" }}
                    />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  PTR: ₹{medicine?.ptr} • Inclusive of all taxes
                </Typography>
              </Paper>

              <Box sx={{ mb: 3 }}>
                {inCart ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: "bold", color: "#008080" }}
                    >
                      Quantity:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        border: "2px solid #008080",
                        borderRadius: 3,
                        overflow: "hidden",
                      }}
                    >
                      <IconButton
                        onClick={decrementQuantity}
                        disabled={quantity <= 0}
                        sx={{
                          backgroundColor: "#008080",
                          color: "white",
                          borderRadius: 0,
                          "&:hover": { backgroundColor: "#006666" },
                          "&:disabled": { backgroundColor: "#ccc" },
                        }}
                      >
                        <RemoveIcon />
                      </IconButton>
                      <TextField
                        size="small"
                        value={quantity}
                        onChange={handleQuantityChange}
                        inputProps={{
                          min: 0,
                          max: 999,
                          style: {
                            textAlign: "center",
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                          },
                        }}
                        sx={{
                          width: "80px",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { border: "none" },
                          },
                        }}
                      />
                      <IconButton
                        onClick={incrementQuantity}
                        disabled={quantity >= 999}
                        sx={{
                          backgroundColor: "#008080",
                          color: "white",
                          borderRadius: 0,
                          "&:hover": { backgroundColor: "#006666" },
                          "&:disabled": { backgroundColor: "#ccc" },
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Button
                    size="large"
                    variant="contained"
                    startIcon={<CartIcon />}
                    onClick={handleAddToCart}
                    sx={{
                      background:
                        "linear-gradient(135deg, #008080 0%, #20B2AA 100%)",
                      borderRadius: 3,
                      py: 1.5,
                      px: 4,
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      boxShadow: "0 4px 15px rgba(0, 128, 128, 0.3)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #006666 0%, #1a9999 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(0, 128, 128, 0.4)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Add to Cart
                  </Button>
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <Chip
                  icon={<ShippingIcon />}
                  label="Free Delivery"
                  variant="outlined"
                  color="primary"
                />
                <Chip
                  icon={<SecurityIcon />}
                  label="Secure Payment"
                  variant="outlined"
                  color="primary"
                />
                <Chip
                  icon={<AssignmentIcon />}
                  label="Prescription Required"
                  variant="outlined"
                  color="warning"
                />
              </Box>
            </Card>
          </Grid>
        </Grid>

        <Card
          sx={{
            mt: 4,
            borderRadius: 3,
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                fontWeight: "bold",
                fontSize: "1rem",
              },
            }}
          >
            <Tab label="Product Details" />
            <Tab label="Composition" />
            <Tab label="Usage & Dosage" />
            <Tab label="Reviews" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {tabValue === 0 && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "#008080", fontWeight: "bold" }}
                >
                  Product Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  {medicine?.product_details ||
                    "Detailed product information will be displayed here."}
                </Typography>

                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Key Features
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>
                      • High quality pharmaceutical product
                      <br />• Manufactured under strict quality controls
                      <br />• Tested for purity and potency
                      <br />• Safe and effective when used as directed
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}

            {tabValue === 1 && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "#008080", fontWeight: "bold" }}
                >
                  Active Ingredients
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                  >
                    Salts Composition:
                  </Typography>
                  <Typography variant="body1">
                    {medicine?.salts ||
                      "Salt composition information will be displayed here."}
                  </Typography>
                </Paper>
              </Box>
            )}

            {tabValue === 2 && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "#008080", fontWeight: "bold" }}
                >
                  Usage Instructions
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Always consult with a healthcare professional before using
                  this medication.
                </Alert>
                <Typography variant="body1">
                  Please follow the dosage instructions provided by your
                  healthcare provider or as mentioned on the product packaging.
                </Typography>
              </Box>
            )}

            {tabValue === 3 && (
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "#008080", fontWeight: "bold" }}
                >
                  Customer Reviews
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Typography variant="h4" sx={{ mr: 2, fontWeight: "bold" }}>
                    4.5
                  </Typography>
                  <Box>
                    <Rating value={4.5} precision={0.5} readOnly />
                    <Typography variant="body2" color="text.secondary">
                      Based on 127 reviews
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body1" color="text.secondary">
                  Customer reviews will be displayed here.
                </Typography>
              </Box>
            )}
          </Box>
        </Card>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity="success"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default DetailPage;
